/**
 * ENG-6383 (epic ENG-6333 — graceful degradation, ENG-6375 Slice C): the pure
 * input-assembly helpers for the manager's safe-resume wiring.
 *
 * The decision itself ({@link ./resume-reconciler.ts} `decideResumeReconcile`)
 * is already pure + tested. Slice C is the I/O glue in the ~8000-line
 * manager-worker hot file: fetch the server-owned health (ENG-6382
 * `GET /host/circuit-breaker/resume-health`) and derive the manager-owned
 * `mcpPresent`. These two assembly steps are extracted here so the parsing +
 * the mcp-presence derivation are unit-testable without the poll loop, leaving
 * the hot-file edit a thin call into tested code.
 *
 * Fail-safe everywhere: a missing/garbled health body, or an unreadable
 * `.mcp.json`, resolves to "NOT healthy" — never to a spurious resume. The
 * whole reconciler exists because a blind resume re-creates the crash-loop, so
 * ambiguity must hold, not resume.
 */

/**
 * Derive the whole-agent `mcpPresent` gate: every MCP server the agent declares
 * in `.mcp.json` is still present (none the presence-reaper has GIVEN UP on).
 * Mirrors Slice B's whole-agent philosophy — there's no server-side
 * trip→dependency link, so the only sound signal is "nothing is missing".
 *
 *   - `declaredKeys === null` (file missing/unparseable) → `false`. We can't
 *     confirm the desired state, so we fail safe and don't resume.
 *   - empty declared set → `true`. No MCP dependency exists to be absent
 *     (vacuous — the mirror of an agent with zero integrations in Slice B).
 *   - any declared key in the given-up set → `false`. A declared server the
 *     reaper abandoned (missing through the full restart budget) is genuinely
 *     gone; resuming into it would just re-trip.
 */
export function deriveMcpPresent(
  declaredKeys: Set<string> | null,
  givenUpKeys: ReadonlySet<string>,
): boolean {
  if (declaredKeys === null) return false;
  for (const key of declaredKeys) {
    if (givenUpKeys.has(key)) return false;
  }
  return true;
}

/** The server-owned half of the health snapshot (ENG-6382 response payload). */
export interface ParsedResumeHealth {
  dependencyStatusActive: boolean;
  connectivityOkCycles: number;
}

/**
 * Defensively parse the `GET /host/circuit-breaker/resume-health` response into
 * the server-owned health fields. Returns `null` on ANY malformed shape so the
 * caller fails safe (treats it as "not healthy yet", never as healthy). The
 * endpoint also returns `integrationCount`, which the whole-agent gate doesn't
 * need here — only the two recovery signals.
 */
export function parseResumeHealthResponse(raw: unknown): ParsedResumeHealth | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.dependencyStatusActive !== 'boolean') return null;
  if (typeof r.connectivityOkCycles !== 'number' || !Number.isFinite(r.connectivityOkCycles)) {
    return null;
  }
  return {
    dependencyStatusActive: r.dependencyStatusActive,
    connectivityOkCycles: r.connectivityOkCycles,
  };
}
